import { useCallback } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { uniqBy } from 'lodash-es';
import { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { CalendarColumnEntity } from '../../components/calendar-view/types';
import { useCalendarStore } from '../../stores/calendar.store';
import { CalendarEntitySchedule, hasCalendarScheduleWorkingHoursSet } from './calendar-schedule-definitions';
import { useGetCalendarEventsV3 } from './use-get-calendar-events-v3';

type CalendarColumnEntityDataParams = {
  calendarEvents: CalendarEvent[];
  scheduleRequests: ScheduleRequestEvent[] | undefined;
  providers: Provider[];
  operatories: Operatory[];
  providerSchedules: CalendarEntitySchedule[];
};

export const useGetCalendarColumnEntityData = ({
  calendarEvents,
  scheduleRequests,
  providers,
  operatories,
  providerSchedules,
}: CalendarColumnEntityDataParams) => {
  const { calendarEntityType } = useCalendarStore(['calendarEntityType']);
  const {
    getProviderCalendarEvents,
    getOperatoryCalendarEvents,
    getEntityWorkingHours,
    getEntityAvailabilityExceptions,
    getEntityExceptionEvents,
    getProviderScheduleRequestEvents,
    getOperatoryScheduleRequestEvents,
  } = useGetCalendarEventsV3();

  const getCalendarColumnEntityData = useCallback(
    (locationId: string): CalendarColumnEntity[] => {
      const providersArr =
        providers
          .filter((provider) => provider.locationId === locationId)
          .map<CalendarColumnEntity | null>((provider) => {
            const providerId = provider.id ?? '';

            const calendarEventList = getProviderCalendarEvents({
              calendarEvents,
              locationId,
              providerId,
            });

            // If provider is archived, show them only if they have events; otherwise show them if they are not hidden
            const isProviderVisible = provider.isArchived ? !!calendarEventList.length : !provider.hidden;
            if (providerId === undefined || providerId === null || !isProviderVisible) {
              return null;
            }

            const workingHours = getEntityWorkingHours({
              schedules: providerSchedules,
              locationId,
              entityId: providerId,
            });

            const { availabilityExceptions, isAllDayOff } = getEntityAvailabilityExceptions({
              schedules: providerSchedules,
              locationId,
              entityId: providerId,
            });

            const exceptionEventList = getEntityExceptionEvents({
              schedules: providerSchedules,
              locationId,
              entityId: providerId,
            });

            const providerSchedule = providerSchedules.find(
              (schedule) => schedule.locationId === locationId && schedule.id === providerId
            );

            const requestEvents = getProviderScheduleRequestEvents({
              scheduleRequests,
              locationId,
              providerId,
              providerName: getFullName(provider),
            });

            return {
              entityId: providerId,
              calendarId: provider.calendarId ?? '',
              firstName: provider.firstName ?? '',
              lastName: provider.lastName ?? '',
              entityName: getFullName(provider),
              src: provider.profile?.picUrl ?? '',
              events: uniqBy([...calendarEventList, ...exceptionEventList], (event) => event.eventId),
              hasAnyWorkingHoursSet: hasCalendarScheduleWorkingHoursSet(providerSchedule),
              workingHours,
              availabilityExceptions,
              isAllDayOff,
              archived: provider.isArchived,
              requestEvents,
            };
          })
          .filter((p) => !!p) ?? [];

      const unassignedProviderEvents = getProviderCalendarEvents({
        calendarEvents,
        locationId,
        providerId: '',
      });

      const unassignedProvider = {
        entityId: '',
        firstName: 'Unassigned',
        lastName: '',
        entityName: 'unassigned',
        src: '',
        events: unassignedProviderEvents,
        hasAnyWorkingHoursSet: false,
        workingHours: [],
        availabilityExceptions: [],
        archived: false,
      };

      const operatoriesArr =
        operatories
          .filter((operatory) => operatory.locationId === locationId)
          .map<CalendarColumnEntity | null>((operatory) => {
            const operatoryId = operatory.id ?? '';

            const calendarEventList = getOperatoryCalendarEvents({
              calendarEvents,
              locationId,
              operatoryId,
            });

            if (operatory.hidden) {
              return null;
            }

            const requestEvents = getOperatoryScheduleRequestEvents({
              scheduleRequests,
              locationId,
              operatoryId,
            });

            const workingHours = getEntityWorkingHours({
              schedules: providerSchedules,
              locationId,
              entityId: operatoryId,
            });

            const { availabilityExceptions, isAllDayOff } = getEntityAvailabilityExceptions({
              schedules: providerSchedules,
              locationId,
              entityId: operatoryId,
            });

            const operatorySchedule = providerSchedules.find(
              (schedule) => schedule.locationId === locationId && schedule.id === operatoryId
            );

            return {
              entityName: operatory.displayName || operatory.externalName || '',
              entityId: operatoryId,
              iconName: operatory.details?.icon?.name as IconName,
              iconColor: operatory.details?.icon?.color,
              events: uniqBy([...calendarEventList], (event) => event.eventId),
              hasAnyWorkingHoursSet: hasCalendarScheduleWorkingHoursSet(operatorySchedule),
              workingHours,
              availabilityExceptions,
              isAllDayOff,
              requestEvents,
            };
          })
          .filter((p) => !!p) ?? [];

      const unassignedOperatoryEvents = getOperatoryCalendarEvents({
        calendarEvents,
        locationId,
        operatoryId: null,
      });

      const unassignedOperatory = {
        firstName: 'Unassigned',
        lastName: '',
        entityName: 'unassigned',
        entityId: '',
        src: '',
        events: unassignedOperatoryEvents,
        hasAnyWorkingHoursSet: false,
        workingHours: [],
        availabilityExceptions: [],
        archived: false,
      };

      if (calendarEntityType === 'operatory') {
        return [...operatoriesArr, ...(unassignedOperatoryEvents.length > 0 ? [unassignedOperatory] : [])];
      }
      return [...providersArr, ...(unassignedProvider.events.length > 0 ? [unassignedProvider] : [])];
    },
    [
      providers,
      getProviderCalendarEvents,
      calendarEvents,
      operatories,
      getOperatoryCalendarEvents,
      calendarEntityType,
      getEntityWorkingHours,
      providerSchedules,
      getEntityAvailabilityExceptions,
      getEntityExceptionEvents,
      getProviderScheduleRequestEvents,
      scheduleRequests,
      getOperatoryScheduleRequestEvents,
    ]
  );

  return { getCalendarColumnEntityData };
};
